import { v7 as uuidv7 } from 'uuid'
import {
  EntityUpdateContext,
  IFunctionStartContext,
  IFunctionStopContext
} from './context.js'
import { FunctionInstanceObj, ProcessInstanceObj } from './objects.js'
import { Prettify } from './types.js'

// Type alias for serializable entities that can be sent to clients
// Represents the full entity structure with all possible properties
export type IEntity = IObj | ICnx

export interface EntityEventsPayload {
  events: EntityEvent[]
  refreshState?: boolean
}

export interface DiagramCellEndpoint {
  cellId: string
  port?: string
}

export interface DiagramCellPortItem {
  id: string
  group?: string
  args?: Record<string, unknown>
}

export interface DiagramCellPorts {
  groups?: Record<string, unknown>
  items?: DiagramCellPortItem[]
}

export interface DiagramCellLabel {
  position?: number
  attrs?: Record<string, unknown>
}

export interface DiagramCell {
  id: string
  cellType: 'obj' | 'cnx' | 'text'
  entityId?: string
  position?: { x: number; y: number }
  size?: { width: number; height: number }
  angle?: number
  z?: number
  source?: DiagramCellEndpoint
  target?: DiagramCellEndpoint
  vertices?: Array<{ x: number; y: number }>
  text?: string
  name?: string
  type?: string
  attrs?: Record<string, unknown>
  ports?: DiagramCellPorts
  connector?: Record<string, unknown>
  labels?: DiagramCellLabel[]
}

interface IEntityBase {
  entityType: 'obj' | 'cnx'
  id: string
  key: string
  typeOf: string
  name?: string
  instanceOf?: string
  instanceOfId: string | undefined
  childOf?: string

  description?: string

  data?: Record<string, unknown>
  tags: {
    [key: string]: string
  }
  attributes: {
    [key: string]: string | number | boolean | object
  }

  createdBy?: string
  createdAt: number
  updatedBy?: string
  updatedAt?: number

  displayName: string
}

export interface IObj extends IEntityBase {
  entityType: 'obj'
}

export interface ICnx extends IEntityBase {
  entityType: 'cnx'

  fromObjId: string
  fromObjKey: string
  fromObjTypeOf: string
  fromObjInstanceOf?: string

  toObjId: string
  toObjKey: string
  toObjTypeOf: string
  toObjInstanceOf?: string
}

abstract class Entity implements IEntityBase {
  abstract readonly entityType: 'obj' | 'cnx'
  readonly id: string
  readonly key: string
  readonly typeOf: string
  instanceOf?: string
  name?: string
  childOf?: string

  description?: string

  data?: Record<string, unknown>
  tags: {
    [key: string]: string
  }
  attributes: {
    [key: string]: string | number | boolean | object
  }

  createdBy?: string
  createdAt: number
  updatedBy?: string
  updatedAt?: number

  get displayName(): string {
    return this.name || this.key
  }

  get instanceOfId(): string | undefined {
    if (this.instanceOf) {
      return `${this.typeOf}/${this.instanceOf}`
    }
    return undefined
  }

  constructor(
    entityOpts: Prettify<
      Partial<IEntityBase> & { id: string; key: string; typeOf: string }
    >
  ) {
    this.id = entityOpts.id
    this.key = entityOpts.key
    this.typeOf = entityOpts.typeOf
    this.createdAt = Date.now() // Default to now if not provided

    if (entityOpts.instanceOf !== undefined) this.instanceOf = entityOpts.instanceOf
    if (entityOpts.name !== undefined) this.name = entityOpts.name
    if (entityOpts.childOf !== undefined) this.childOf = entityOpts.childOf
    if (entityOpts.description !== undefined) this.description = entityOpts.description
    if (entityOpts.data !== undefined) this.data = entityOpts.data
    this.tags = entityOpts.tags || {}
    this.attributes = entityOpts.attributes || {}
    if (entityOpts.createdBy !== undefined) this.createdBy = entityOpts.createdBy
    if (entityOpts.createdAt !== undefined) this.createdAt = entityOpts.createdAt
    if (entityOpts.updatedBy !== undefined) this.updatedBy = entityOpts.updatedBy
    if (entityOpts.updatedAt !== undefined) this.updatedAt = entityOpts.updatedAt
  }
}

export abstract class Obj<T extends string = string> extends Entity implements IObj {
  readonly entityType = 'obj' as const
  abstract override readonly typeOf: T

  public constructor(objOpts: Prettify<Partial<IObj> & { typeOf: string }>) {
    let id: string
    let key: string
    let typeOf: string

    if (objOpts.id) {
      id = objOpts.id
      const { key: parsedKey, typeOf: parsedTypeOf } = parseCombinedId(objOpts.id)
      key = parsedKey
      typeOf = parsedTypeOf
    } else {
      typeOf = objOpts.typeOf
      key = objOpts.key || uuidv7()
      id = typeOf + '/' + key
    }
    objOpts.id = id
    objOpts.key = key
    objOpts.typeOf = typeOf

    super(objOpts as Partial<IEntityBase> & { id: string; key: string; typeOf: string })
  }
}

export class Cnx extends Entity implements ICnx {
  readonly entityType = 'cnx'
  override readonly typeOf: string

  fromObjId: string
  fromObjKey: string
  fromObjTypeOf: string
  fromObjInstanceOf?: string

  toObjId: string
  toObjKey: string
  toObjTypeOf: string
  toObjInstanceOf?: string
  public constructor(
    cnxOpts: Prettify<
      (Partial<ICnx> & { id: string }) | (Partial<ICnx> & { typeOf: string })
    >
  ) {
    let id: string
    let key: string
    let typeOf: string
    if (cnxOpts.id) {
      id = cnxOpts.id
      const { key: parsedKey, typeOf: parsedTypeOf } = parseCombinedId(cnxOpts.id)
      key = parsedKey
      typeOf = parsedTypeOf ?? cnxOpts.typeOf
      if (cnxOpts.typeOf && parsedTypeOf !== cnxOpts.typeOf) {
        throw new Error(`Type mismatch: expected ${cnxOpts.typeOf}, got ${parsedTypeOf}`)
      }
    } else if (cnxOpts.typeOf) {
      typeOf = cnxOpts.typeOf
      key = cnxOpts.key || uuidv7()
      id = typeOf + '/' + key
    } else {
      throw new Error('Either id or typeOf must be provided for Cnx')
    }

    cnxOpts.id = id
    cnxOpts.key = key
    cnxOpts.typeOf = typeOf
    super(cnxOpts as Partial<IEntityBase> & { id: string; key: string; typeOf: string })

    this.typeOf = typeOf
    if (cnxOpts.fromObjId) {
      this.fromObjId = cnxOpts.fromObjId
      const { key: fromKey, typeOf: fromTypeOf } = parseCombinedId(cnxOpts.fromObjId)
      this.fromObjKey = fromKey
      this.fromObjTypeOf = fromTypeOf
    } else if (cnxOpts.fromObjKey && cnxOpts.fromObjTypeOf) {
      this.fromObjKey = cnxOpts.fromObjKey
      this.fromObjTypeOf = cnxOpts.fromObjTypeOf
      this.fromObjId = cnxOpts.fromObjTypeOf + '/' + cnxOpts.fromObjKey
    } else {
      throw new Error('fromObjId or fromObjKey and fromObjTypeOf are required for Cnx')
    }
    if (cnxOpts.fromObjInstanceOf !== undefined) {
      this.fromObjInstanceOf = cnxOpts.fromObjInstanceOf
    }

    if (cnxOpts.toObjId) {
      this.toObjId = cnxOpts.toObjId
      const { key: toKey, typeOf: toTypeOf } = parseCombinedId(cnxOpts.toObjId)
      this.toObjKey = toKey
      this.toObjTypeOf = toTypeOf
    } else if (cnxOpts.toObjKey && cnxOpts.toObjTypeOf) {
      this.toObjKey = cnxOpts.toObjKey
      this.toObjTypeOf = cnxOpts.toObjTypeOf
      this.toObjId = cnxOpts.toObjTypeOf + '/' + cnxOpts.toObjKey
    } else {
      throw new Error('toObjId or toObjKey and toObjTypeOf are required for Cnx')
    }
    if (cnxOpts.toObjInstanceOf !== undefined) {
      this.toObjInstanceOf = cnxOpts.toObjInstanceOf
    }
  }
}

export type EangEventTypes = (typeof EangEventType)[keyof typeof EangEventType]
export const EangEventType = {
  create: 'create',
  update: 'update',
  delete: 'delete',
  start: 'start',
  stop: 'stop'
} as const

export interface EntityEventOptions {
  tenant: string
  organizationalUnit: string
  user: string
}

export class EntityEvent {
  entity: Obj | Cnx
  eventType: EangEventTypes
  context?: any
  tenant?: string
  organizationalUnit?: string
  user?: string

  private constructor(
    entity: Obj | Cnx,
    eventType: EangEventTypes,
    context?: any,
    options?: EntityEventOptions
  ) {
    this.entity = entity
    this.eventType = eventType
    this.context = context
    if (options?.tenant !== undefined) {
      this.tenant = options.tenant
    }
    if (options?.organizationalUnit !== undefined) {
      this.organizationalUnit = options.organizationalUnit
    }
    if (options?.user !== undefined) {
      this.user = options.user
    }
  }

  public static parse(
    entity: Obj | Cnx,
    eventType: EangEventTypes,
    context?: any,
    options?: EntityEventOptions
  ): EntityEvent {
    return new EntityEvent(entity, eventType, context, options)
  }

  public static create<T extends IEntity>(entity: T, options?: EntityEventOptions) {
    if (!entity.createdAt) {
      entity.createdAt = Date.now()
    }
    return new EntityEvent(entity, EangEventType.create, undefined, options)
  }

  public static update<T extends IEntity>(
    entity: T,
    context?: EntityUpdateContext,
    options?: EntityEventOptions
  ) {
    entity.updatedAt = Date.now()
    // Apply new values to the entity before creating the event

    return new EntityEvent(entity, EangEventType.update, context, options)
  }

  public static delete<T extends IEntity>(entity: T, options?: EntityEventOptions) {
    return new EntityEvent(entity, EangEventType.delete, undefined, options)
  }

  public static start(
    entity: FunctionInstanceObj | ProcessInstanceObj,
    context?: IFunctionStartContext,
    options?: EntityEventOptions
  ) {
    if ('createdAt' in entity && !entity.createdAt) {
      entity.createdAt = Date.now()
    }
    if (!('startedAt' in entity) || !entity.startedAt) {
      entity.startedAt = Date.now()
    }
    return new EntityEvent(entity, EangEventType.start, context, options)
  }

  public static stop(
    entity: ProcessInstanceObj | FunctionInstanceObj,
    context?: IFunctionStopContext,
    options?: EntityEventOptions
  ) {
    if (!('stoppedAt' in entity) || !entity.stoppedAt) {
      entity.stoppedAt = Date.now()
    }
    return new EntityEvent(entity, EangEventType.stop, context, options)
  }
}

export function parseCombinedId(id: string) {
  const sepIdx = id.indexOf('/')
  if (sepIdx === -1 || sepIdx === 0 || sepIdx === id.length - 1) {
    throw new Error(`Invalid ID format: ${id}`)
  }
  return { typeOf: id.slice(0, sepIdx), key: id.slice(sepIdx + 1) }
}

/**
 * Converts an entity (Obj or Cnx) to ArangoDB document format
 * @param entity The entity to convert
 * @returns ArangoDB document with proper _key, _from, _to fields
 */
export function toArangoDoc(entity: Obj | Cnx): Record<string, unknown> {
  // Prepare the document for ArangoDB
  const arangoDoc: Record<string, unknown> = {
    _key: entity.key,
    ...entity
  }

  // Remove fields that shouldn't be stored or are redundant
  delete arangoDoc.key
  delete arangoDoc.entityType
  delete arangoDoc.id

  // Remove empty tags and attributes objects
  if (
    arangoDoc.tags &&
    typeof arangoDoc.tags === 'object' &&
    Object.keys(arangoDoc.tags).length === 0
  ) {
    delete arangoDoc.tags
  }
  if (
    arangoDoc.attributes &&
    typeof arangoDoc.attributes === 'object' &&
    Object.keys(arangoDoc.attributes).length === 0
  ) {
    delete arangoDoc.attributes
  }

  // Handle connection entities differently
  if (entity.entityType === 'cnx') {
    // For connections, we need to set _from and _to for ArangoDB edge collection
    arangoDoc._from = entity.fromObjId
    arangoDoc._to = entity.toObjId

    // Remove the redundant connection fields that ArangoDB doesn't need
    delete arangoDoc.fromObjId
    delete arangoDoc.fromObjKey
    delete arangoDoc.fromObjTypeOf
    delete arangoDoc.fromObjInstanceOf
    delete arangoDoc.toObjId
    delete arangoDoc.toObjKey
    delete arangoDoc.toObjTypeOf
    delete arangoDoc.toObjInstanceOf
  } else {
    delete arangoDoc.typeOf
  }

  return arangoDoc
}
